import { type Country, type Zone, createInitialZonesState, getAllCountries } from '@/app/data/zones';
import { Button } from '@/components/ui/button';
import { IconTrash } from '@/components/ui/icons';
import Modal from '@/components/ui/modal';
import type { CountryIsoCode } from '@/graphql/graphql';
import { Save } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import ExcludedCountryItem from '../../excluded-country-item';
import CustomCheckBox from '../custom-checkbox';

import {
    type ExclusionTemplate,
    createExclusionTemplate,
    deleteExclusionTemplate,
    fetchExclusionTemplates,
    renameExclusionTemplate,
} from '@/lib/api-exclusion-templates';

import ConfirmationCloseModal from '../confirmation-close-modal';
import ConfirmationRemoveAllModal from '../confirmation-remove-all-modal';
import ConfirmationRemoveRegionModal from '../confirmation-remove-region-modal';
import ConfirmationSaveModal from '../confirmation-save-modal';
import NewTemplateModal from '../new-template-modal';

import { useUserData } from '@/context/auth/user-data-context';
import { set } from 'date-fns';
import RestrictionTypeModal from '../restriction-type-modal';
import {
    ExcludedCountriesModalsProps,
    ModalMode,
    RestrictionType,
    RestrictionTypeKey,
    TranslatableRestrictionType,
    UICountry,
    ZoneLabelKey,
} from './interfaces';

export const ExcludedCountriesModals = ({
    isOpen,
    onClose,
    initialExcludedCountries = [],
    onSave,
}: ExcludedCountriesModalsProps) => {
    const t = useTranslations();
    const tc = useTranslations('IsoCountry');
    const td = useTranslations('IsoCountryDesc');

    const [isRestrictionTypeModalOpen, setIsRestrictionTypeModalOpen] = useState<ModalMode | undefined>(undefined);
    const [restrictedZones, setRestrictedZones] = useState<Zone[]>(createInitialZonesState());
    const [restrictedCountries, setRestrictedCountries] = useState<UICountry[]>([]);
    const [activeZone, setActiveZone] = useState<string>('');
    const [initialCountriesSnapshot, setInitialCountriesSnapshot] = useState<UICountry[]>([]);

    const [selectedZoneCode] = useState<string>('');
    const [selectedCountries, setSelectedCountries] = useState<UICountry[]>([]);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
    const [currentRestrictionType, setCurrentRestrictionType] = useState<RestrictionType[]>([]);

    const [confirmationCloseModal, setConfirmationCloseModal] = useState<boolean>(false);
    const [confirmationSaveModal, setConfirmationSaveModal] = useState<boolean>(false);
    const [confirmationRemoveAllModal, setConfirmationRemoveAllModal] = useState<boolean>(false);
    const [newTemplateModalOpen, setNewTemplateModalOpen] = useState<boolean>(false);

    const [hasChanges, setHasChanges] = useState<boolean>(false);

    const inputNewTemplateName = useRef<HTMLInputElement>(null);

    const [savedTemplates, setSavedTemplates] = useState<ExclusionTemplate[]>([]);
    const [currentSelectionForTemplate, setCurrentSelectionForTemplate] = useState<UICountry[]>([]);
    const [saveError, setSaveError] = useState<string | null>(null);
    const countriesZoneButtonClassName = `
    flex-1 whitespace-normal! leading-[24px] py-[14px] px-[24px] h-[70px]
    text-sm text-white bg-[#000000]
    rounded-[8px] border border-1 border-white
    hover:bg-[#B10DCE] hover:border-[#B10DCE]
    transition-all duration-300 cursor-pointer`;
    const ecmId: string = 'excludedCountriesModal';
    const { isAdmin } = useUserData();

    function restrictionTypeKey(rt: Exclude<RestrictionType, ''>): RestrictionTypeKey {
        return `ScreeningRoom.modal.restrictedCountry.restrictionType.${rt}`;
    }

    const formatRestrictionType = (keys: TranslatableRestrictionType[]): string => {
        const formattedKeys = keys.map((key) => t(restrictionTypeKey(key)));
        return `(${formattedKeys.join(' + ')})`;
    };

    const toRestriction = (exclusionType: string): RestrictionType => {
        switch (exclusionType.toUpperCase()) {
            case 'FAST':
                return 'FAST';
            case 'LOCAL_VOD':
                return 'local_vod';
            default:
                return 'classic';
        }
    };

    const toExclusionType = (restriction: RestrictionType): string => {
        switch (restriction) {
            case 'FAST':
                return 'FAST';
            case 'local_vod':
                return 'LOCAL_VOD';
            default:
                return 'CLASSIC';
        }
    };

    const addRestriction = (restrictions: RestrictionType[], restriction: RestrictionType) => {
        if (!restrictions.includes(restriction)) {
            restrictions.push(restriction);
        }
    };

    const normalizeExclusionType = (exclusionType: string): RestrictionType[] => {
        const normalized = exclusionType.toUpperCase();

        if (normalized === 'ALL') {
            return ['FAST', 'classic', 'local_vod'];
        }

        return [toRestriction(exclusionType)];
    };

    const groupExcludedCountriesByCountry = (excludedCountries: ExclusionTemplate['excludedCountries']) => {
        const grouped = new Map<string, RestrictionType[]>();

        for (const excludedCountry of excludedCountries) {
            const countryISOCode = excludedCountry.countryISOCode.toUpperCase();
            const restrictions = grouped.get(countryISOCode) ?? [];

            for (const restriction of normalizeExclusionType(excludedCountry.exclusionType)) {
                addRestriction(restrictions, restriction);
            }

            grouped.set(countryISOCode, restrictions);
        }

        return grouped;
    };

    const serializeSelection = (selection: UICountry[]) =>
        selection
            .flatMap((country) =>
                (country.restrictionType ?? [])
                    .filter((restriction): restriction is Exclude<RestrictionType, ''> => restriction !== '')
                    .map((restriction) => ({
                        countryISOCode: country.id.toUpperCase(),
                        exclusionType: toExclusionType(restriction),
                    })),
            )
            .sort(
                (a, b) =>
                    a.countryISOCode.localeCompare(b.countryISOCode) || a.exclusionType.localeCompare(b.exclusionType),
            );

    const serializeTemplate = (template: ExclusionTemplate) =>
        Array.from(groupExcludedCountriesByCountry(template.excludedCountries).entries())
            .flatMap(([countryISOCode, restrictions]) =>
                restrictions.map((restriction) => ({
                    countryISOCode,
                    exclusionType: toExclusionType(restriction),
                })),
            )
            .sort(
                (a, b) =>
                    a.countryISOCode.localeCompare(b.countryISOCode) || a.exclusionType.localeCompare(b.exclusionType),
            );

    useEffect(() => {
        if (!isOpen) return;

        const loadTemplates = async () => {
            try {
                const templates = await fetchExclusionTemplates();
                setSavedTemplates(templates);
            } catch (error) {
                void error;
            } finally {
                // do nothing
            }
        };

        if (!isAdmin) loadTemplates();
    }, [isOpen, isAdmin]);

    useEffect(() => {
        if (!isOpen) return;

        document.body.style.overflow = 'hidden';

        const updatedCountries: UICountry[] = getAllCountries().map((country) => {
            const excludedCountry = initialExcludedCountries.find((excluded) => excluded.country === country.id);
            return {
                ...country,
                active: !!excludedCountry,
                restrictionType: excludedCountry?.type ?? [],
            };
        });

        const sorted: UICountry[] = [...updatedCountries].sort((a, b) =>
            tc(a.id as CountryIsoCode).localeCompare(tc(b.id as CountryIsoCode)),
        );

        setRestrictedCountries(sorted);
        setInitialCountriesSnapshot(JSON.parse(JSON.stringify(sorted)));
        setHasChanges(false);

        const initialSelectedCountries = sorted.filter((country) => country.active);
        setSelectedCountries(initialSelectedCountries);

        setRestrictedZones((prevZones) =>
            prevZones.map((zone) => {
                const countriesInZone = sorted.filter((c) => c.parentId === zone.id);
                const activeCountriesInZone = countriesInZone.filter((c) => c.active);
                return {
                    ...zone,
                    active: countriesInZone.length > 0 && activeCountriesInZone.length === countriesInZone.length,
                };
            }),
        );
    }, [isOpen, initialExcludedCountries, tc]);

    useEffect(() => {
        const hasChanged = JSON.stringify(restrictedCountries) !== JSON.stringify(initialCountriesSnapshot);
        setHasChanges(hasChanged);
    }, [restrictedCountries, initialCountriesSnapshot]);

    const toggleCountryActive = (countryCode: string, restrictionType: RestrictionType[]) => {
        const countryExist = selectedCountries.find((c) => c.id === countryCode);
        if (countryExist) {
            const updatedSelected = selectedCountries.filter((c) => c.id !== countryCode);
            setSelectedCountries(updatedSelected);
        } else {
            const countryToAdd = restrictedCountries.find((c) => c.id === countryCode);
            if (countryToAdd) {
                const updatedCountry = {
                    ...countryToAdd,
                    active: true,
                    restrictionType,
                };
                setSelectedCountries([...selectedCountries, updatedCountry]);
            }
        }
    };

    const openRestrictionTypeModal = (event: MouseEvent<HTMLElement>, code: string, selectAll: boolean) => {
        event.preventDefault();
        event.stopPropagation();

        setIsRestrictionTypeModalOpen(selectAll ? ModalMode.ALL_COUNTRIES : ModalMode.SINGLE_COUNTRY);
        setSelectedCountryCode(code);
        const country = restrictedCountries.find((c) => c.id === code);
        setCurrentRestrictionType(country?.restrictionType ?? []);
    };

    const toggleRestrictionType = (type: RestrictionType) => {
        setCurrentRestrictionType((prev) => {
            const exists = prev.includes(type);
            if (exists) {
                return prev.filter((t) => t !== type);
            }
            return [...prev, type];
        });
    };

    const handleMainModalClose = () => {
        if (hasChanges) {
            setConfirmationCloseModal(true);
        } else {
            onClose();
        }
        setActiveZone('');
    };

    const handleSaveWithTemplateCheck = () => {
        const activeCountries = selectedCountries.filter((country) => country.active);
        const matchingTemplate = findMatchingTemplate(activeCountries);
        saveSelection();
        setActiveZone('');
        if (!matchingTemplate && !isAdmin && activeCountries.length > 0) {
            setCurrentSelectionForTemplate(activeCountries);
            setNewTemplateModalOpen(true);
        } else {
            onClose();
        }
    };

    const handleSaveAndClose = () => {
        handleSaveWithTemplateCheck();
        setActiveZone('');
        setConfirmationCloseModal(false);
    };

    const handleCloseWithoutSaving = () => {
        onClose();
        setActiveZone('');
        setConfirmationCloseModal(false);
    };

    const saveSelection = () => {
        const activeCountriesWithRestrictions = selectedCountries
            .filter((country) => country.active)
            .map((country) => ({
                country: country.id as CountryIsoCode,
                type: country.restrictionType ?? [],
            }));
        onSave(activeCountriesWithRestrictions);
    };

    const handleRestrictionTypeConfirm = () => {
        if (isRestrictionTypeModalOpen === ModalMode.ALL_COUNTRIES) {
            toggleAllRestrictions(currentRestrictionType, true);
        } else {
            toggleCountryActive(selectedCountryCode, currentRestrictionType);
        }
        setIsRestrictionTypeModalOpen(undefined);
    };

    const saveNewTemplate = async (templateName: string) => {
        if (!templateName || templateName.trim() === '') {
            setSaveError(t('ScreeningRoom.modal.excludeTemplate.errors.templateName'));
            return;
        }

        if (currentSelectionForTemplate.length === 0) {
            setSaveError(t('ScreeningRoom.modal.excludeTemplate.errors.noCountries'));
            setNewTemplateModalOpen(false);
            return;
        }

        try {
            setSaveError(null);

            const excludedCountries = serializeSelection(currentSelectionForTemplate).map((country) => ({
                countryISOCode: country.countryISOCode as CountryIsoCode,
                exclusionType: country.exclusionType,
            }));

            await createExclusionTemplate({
                name: templateName.trim(),
                excludedCountries,
            });

            setNewTemplateModalOpen(false);
            setCurrentSelectionForTemplate([]);
            onClose();

            if (inputNewTemplateName.current) {
                inputNewTemplateName.current.value = '';
            }
        } catch (error) {
            void error;
            setSaveError(t('ScreeningRoom.modal.excludeTemplate.errors.failedToSave'));
        }
    };
    const handleRestrictionTypeCancel = () => {
        setIsRestrictionTypeModalOpen(undefined);
    };

    const findMatchingTemplate = (selection: UICountry[]): ExclusionTemplate | null => {
        if (selection.length === 0) return null;

        const currentSelection = serializeSelection(selection);

        for (const template of savedTemplates) {
            const templateSelection = serializeTemplate(template);

            if (
                currentSelection.length === templateSelection.length &&
                currentSelection.every(
                    (curr, index) =>
                        curr.countryISOCode === templateSelection[index].countryISOCode &&
                        curr.exclusionType === templateSelection[index].exclusionType,
                )
            ) {
                return template;
            }
        }

        return null;
    };

    const filterByZone = (zone: Zone) => {
        if (restrictedCountries !== initialCountriesSnapshot && zone.id === activeZone) {
            setRestrictedCountries(initialCountriesSnapshot);
            setActiveZone('');
            return;
        }
        const countriesInZone = initialCountriesSnapshot.filter((country) => country.parentId === zone.id);
        setRestrictedCountries(countriesInZone);
        setActiveZone(zone.id);
    };

    const toggleAllRestrictions = (type: RestrictionType[], check: boolean) => {
        const idList: string[] = restrictedCountries.map((c) => c.id);
        const filteredCountriesList = selectedCountries.filter((c) => !idList.includes(c.id));
        if (check) {
            restrictedCountries.map((c) =>
                filteredCountriesList.push({
                    ...c,
                    active: true,
                    restrictionType: type,
                }),
            );
        }
        setSelectedCountries(filteredCountriesList);
    };

    const targetColumns = savedTemplates.length > 0 ? 5 : 6;
    const visibleColumns = targetColumns;
    const baseColumnSize = Math.floor(restrictedCountries.length / visibleColumns);
    const remainingItems = restrictedCountries.length % visibleColumns;
    const countriesByColumn: Array<{ key: string; countries: UICountry[] }> = [];

    let cursor = 0;
    for (let index = 0; index < visibleColumns; index++) {
        const columnSize = baseColumnSize + (index < remainingItems ? 1 : 0);
        countriesByColumn.push({
            key: `column-${index}`,
            countries: restrictedCountries.slice(cursor, cursor + columnSize),
        });
        cursor += columnSize;
    }

    return (
        <>
            <Modal
                rounded="rounded-0"
                closePosition="flex justify-end w-full pt-[30px] pe-[21px] static!"
                bg="bg-[#0E0E0E]"
                isOpen={isOpen}
                onClose={handleMainModalClose}
                ModalSize="fixed inset-0 w-screen h-screen"
                modalPadding="p-0"
                modalId={ecmId}
            >
                <div className="flex flex-col h-full">
                    <div className="flex flex-col gap-[10px] p-[18px] px-[65px]">
                        <h3 className="text-center font-bold text-2xl mx-auto">
                            {t.rich('ScreeningRoom.modal.restrictedCountry.title')}{' '}
                            {t.rich('ScreeningRoom.modal.restrictedCountry.title2', {
                                b: (chunks: ReactNode) => <b className="text-[#DD06AE]">{chunks}</b>,
                            })}
                        </h3>

                        <div className="text-center">
                            {t('ScreeningRoom.modal.restrictedCountry.paragraph')}
                            <div className="flex gap-4 justify-center mt-[67px] mb-[50px] mx-auto">
                                {restrictedZones.map((zone: Zone) => (
                                    <Button
                                        key={zone.id}
                                        className={countriesZoneButtonClassName}
                                        onClick={() => filterByZone(zone)}
                                        style={
                                            activeZone === zone.id
                                                ? {
                                                      backgroundColor: '#DD06AE',
                                                      borderColor: '#DD06AE',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {t(zone.label as ZoneLabelKey)}
                                    </Button>
                                ))}
                            </div>

                            <div className="flex gap-[39px]">
                                {savedTemplates.length > 0 && (
                                    <div
                                        style={{
                                            width: `calc((100vw - 130px - ${16 * (restrictedZones.length - 1)}px) / ${restrictedZones.length} - 2px)`,
                                        }}
                                    >
                                        <p className="text-[14px] font-[700] text-start mb-[22px]">
                                            {t('ScreeningRoom.modal.excludeTemplate.listTitle')}
                                        </p>

                                        <ul className="space-y-[30px]">
                                            {savedTemplates.map((template) => (
                                                <li key={template.id}>
                                                    <ExcludedCountryItem
                                                        parentModalId={ecmId}
                                                        name={template.name}
                                                        id={template.id}
                                                        onRename={async (newName) => {
                                                            try {
                                                                await renameExclusionTemplate(
                                                                    template.id,
                                                                    newName,
                                                                    template.excludedCountries,
                                                                );
                                                                const templates = await fetchExclusionTemplates();
                                                                setSavedTemplates(templates);
                                                            } catch (error) {
                                                                void error;
                                                            }
                                                        }}
                                                        onDelete={async (id) => {
                                                            try {
                                                                await deleteExclusionTemplate(id);
                                                            } catch (error: unknown) {
                                                                void error;
                                                            }

                                                            try {
                                                                const templates = await fetchExclusionTemplates();
                                                                setSavedTemplates(templates);
                                                            } catch (error: unknown) {
                                                                void error;
                                                            }
                                                        }}
                                                        onApply={() => {
                                                            const groupedExcludedCountries =
                                                                groupExcludedCountriesByCountry(
                                                                    template.excludedCountries,
                                                                );
                                                            const filteredCountries: UICountry[] =
                                                                initialCountriesSnapshot
                                                                    .filter((country) =>
                                                                        groupedExcludedCountries.has(country.id),
                                                                    )
                                                                    .map((country) => ({
                                                                        ...country,
                                                                        active: true,
                                                                        restrictionType:
                                                                            groupedExcludedCountries.get(country.id) ??
                                                                            [],
                                                                    }));
                                                            setSelectedCountries(filteredCountries);

                                                            return filteredCountries;
                                                        }}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div
                                    className="grid gap-x-[8px] py-[20px] px-[25px] rounded-[8px] bg-[rgba(255,255,255,0.10)] flex-1"
                                    style={{
                                        gridTemplateColumns: `repeat(${visibleColumns}, minmax(0, 1fr))`,
                                    }}
                                >
                                    {countriesByColumn.map((column) => (
                                        <div key={column.key} className="flex flex-col gap-y-[8px] content-start">
                                            {column.countries.map((country) => {
                                                const countryFromSelectedList = selectedCountries?.find(
                                                    (c) => c.id === country.id,
                                                );
                                                const hasRestrictions =
                                                    (countryFromSelectedList?.restrictionType?.length ?? 0) > 0;
                                                const showAll =
                                                    (countryFromSelectedList?.restrictionType?.length ?? 0) === 3;
                                                const first = (countryFromSelectedList?.restrictionType ?? []).filter(
                                                    (rt): rt is TranslatableRestrictionType => rt !== '',
                                                );
                                                return (
                                                    <label
                                                        key={country.id}
                                                        className="flex text-left cursor-pointer px-[10px] py-[5px]"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="cursor-pointer hidden peer"
                                                            checked={!!countryFromSelectedList}
                                                            readOnly
                                                            onClick={(event) => {
                                                                if (countryFromSelectedList) {
                                                                    toggleCountryActive(country.id, []);
                                                                } else {
                                                                    openRestrictionTypeModal(event, country.id, false);
                                                                }
                                                            }}
                                                        />
                                                        <CustomCheckBox customClassName="relative top-[3px] mr-[10px]" />{' '}
                                                        <span
                                                            className="text-ellipsis overflow-hidden flex-1"
                                                            title={td(country.id as CountryIsoCode)}
                                                        >
                                                            {tc(country.id as CountryIsoCode)}{' '}
                                                            {hasRestrictions &&
                                                                (!showAll && first.length > 0
                                                                    ? formatRestrictionType(first)
                                                                    : '')}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="pt-[50px] pb-[32px] flex gap-[20px] justify-center">
                                <Button
                                    onClick={() => {
                                        setConfirmationRemoveAllModal(true);
                                    }}
                                    className="bg-[#0e0e0e] border-1 border-[#fff] hover:bg-[#B10DCE] hover:border-[#B10DCE] text-white px-[20px]! py-[8px] cursor-pointer"
                                >
                                    <IconTrash className="size-[20px] w-[20px] h-[20px]" viewBox="0 0 20 20" />
                                    {t('ScreeningRoom.modal.buttons.remove')}
                                </Button>
                                <Button
                                    onClick={(event) => {
                                        openRestrictionTypeModal(event, '', true);
                                    }}
                                    className="bg-[#0e0e0e] border-1 border-[#fff] hover:bg-[#B10DCE] hover:border-[#B10DCE] text-white px-[20px]! py-[8px] cursor-pointer"
                                >
                                    <Save className="size-[20px] w-[20px] h-[20px]" />
                                    {t('ScreeningRoom.modal.buttons.applyAll')}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setConfirmationSaveModal(true);
                                    }}
                                    className="bg-[#DD06AE] hover:bg-[#B10DCE] text-white px-[20px]! py-[8px] cursor-pointer"
                                >
                                    <Save className="size-[20px] w-[20px] h-[20px]" />
                                    {t('ScreeningRoom.modal.buttons.save')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            <RestrictionTypeModal
                isOpen={isRestrictionTypeModalOpen}
                onClose={handleRestrictionTypeCancel}
                isRestrictionZoneChoice={false}
                selectedZoneCode={selectedZoneCode}
                selectedCountryCode={selectedCountryCode}
                currentRestrictionType={currentRestrictionType}
                onRestrictionTypeChange={toggleRestrictionType}
                onApply={handleRestrictionTypeConfirm}
            />

            <ConfirmationCloseModal
                isOpen={confirmationCloseModal}
                onClose={() => setConfirmationCloseModal(false)}
                onSaveAndClose={handleSaveAndClose}
                onCloseWithoutSaving={handleCloseWithoutSaving}
            />

            <ConfirmationSaveModal
                isOpen={confirmationSaveModal}
                onClose={() => setConfirmationSaveModal(false)}
                onConfirm={() => {
                    handleSaveWithTemplateCheck();
                    setConfirmationSaveModal(false);
                }}
                onCancel={() => setConfirmationSaveModal(false)}
            />

            <ConfirmationRemoveAllModal
                isOpen={confirmationRemoveAllModal}
                onClose={() => setConfirmationRemoveAllModal(false)}
                onConfirm={() => {
                    toggleAllRestrictions([], false);
                    setConfirmationRemoveAllModal(false);
                }}
                onCancel={() => setConfirmationRemoveAllModal(false)}
            />
            <NewTemplateModal
                isOpen={newTemplateModalOpen}
                onClose={() => {
                    setNewTemplateModalOpen(false);
                    setSaveError(null);
                    setCurrentSelectionForTemplate([]);
                    onClose();
                }}
                onSave={saveNewTemplate}
                saveError={saveError}
            />
        </>
    );
};
